import { Component, ElementRef, OnInit, ViewChild, signal } from '@angular/core';
import { Role } from '../enums';
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatChipInputEvent,
  MatChipGrid,
  MatChipRow,
  MatChipRemove,
  MatChipInput,
} from '@angular/material/chips';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import {
  Characteristic,
  DataStoreExport,
  PaidFunctionalities,
  Particularity,
  PublicSettings,
  Ride,
  Setting,
  User,
  Vehicle,
} from '../interfaces';
import { FileSaverService } from 'ngx-filesaver';
import moment from 'moment';
import 'moment/locale/nl';
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  deleteField,
  updateDoc,
} from 'firebase/firestore';
import {
  getFileExtension,
  getFileFromUrl,
  logSettingsChange,
  redirectIfNotAdmin,
  updateTheme,
  exportJsonToExcel,
  valueToDate,
} from '../globals';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { dataStore, db, storage } from 'src/app/app.component';
import { ImportExportComponent } from './dialogs/import-export/import-export.component';
import { ExportRidesComponent } from './dialogs/export-rides/export-rides.component';
import { CustomValidators } from 'angular-custom-validators';
import { catchError, debounceTime, map, of, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { MatIcon } from '@angular/material/icon';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ColorPickerDirective } from 'ngx-color-picker';
import { MatIconButton, MatButton, MatFabButton } from '@angular/material/button';
import { NgStyle, NgClass } from '@angular/common';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  imports: [
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatTabGroup,
    MatTab,
    MatCard,
    MatFormField,
    MatLabel,
    MatInput,
    ColorPickerDirective,
    MatIconButton,
    MatSuffix,
    NgStyle,
    NgClass,
    MatSelect,
    MatOption,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatChipInput,
    MatSlideToggle,
    MatError,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatButton,
    MatCheckbox,
    MatFabButton,
    MatProgressSpinner,
    MatTooltip,
  ],
})
export class SettingsComponent implements OnInit {
  loadingPage = signal(true);
  @ViewChild('uploader', { static: false }) uploader: ElementRef;
  generalSettings: Setting = dataStore.generalSettings;
  publicSettings: PublicSettings = dataStore.publicSettings;
  organisationForm: UntypedFormGroup;
  organisationId = dataStore.selectedOrganisation.id;
  cpToggle1: boolean = false;
  cpValue1: string;
  cpToggle2: boolean = false;
  cpValue2: string;
  cpToggle3: boolean = false;
  cpValue3: string;
  usesDriverPlanning: boolean = localStorage.getItem('usesDriverPlanning')
    ? JSON.parse(localStorage.getItem('usesDriverPlanning'))
    : false;
  usesDriverRemarksInApp: boolean = localStorage.getItem('usesDriverRemarksInApp')
    ? JSON.parse(localStorage.getItem('usesDriverRemarksInApp'))
    : false;
  usesUserNotificationSystem: boolean = localStorage.getItem('usesUserNotificationSystem')
    ? JSON.parse(localStorage.getItem('usesUserNotificationSystem'))
    : false;
  minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  reminderMinutes = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  travelMinutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  hours = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
  segmentMinutes = [5, 10, 15, 20, 25, 30];
  faviconPreview: string;
  bannerPreview: string;
  logoPreview: string;
  loginImagePreview: string;
  saving = signal(false);
  transfering = signal(false);
  exportingRides = signal(false);
  isSuperUser: boolean = dataStore.rootUser.rights === 'admin' ? true : false;
  dataStore: DataStoreExport = dataStore;
  defaultStartPrediction = signal<{ address: string; lat: any; long: any }[]>([]);
  readonly separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];

  constructor(
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private _FileSaverService: FileSaverService,
    private router: Router,
    private titleService: Title,
    private http: HttpClient,
  ) {}

  async ngOnInit(): Promise<void> {
    this.organisationForm = this.fb.group({
      name: [{ value: '', disabled: true }],
      primaryColor: [''],
      accentColor: [''],
      favicon: [],
      banner: [],
      logo: [],
      loginImage: [],
      privacyLink: ['', [Validators.required]],
      maxActiveVehicles: [{ value: '', disabled: true }],
      signalColor: [''],
      pinAddCredits: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
      pinSubtractCredits: [
        '',
        [Validators.required, Validators.minLength(4), Validators.maxLength(4)],
      ],
      askPin: [true],
      askKilometers: [false],
      driversModifyAvailability: [false],
      coDriversModifyAvailability: [false],
      plannersModifyAvailability: [false],
      driversShowParticipantPhone: [false],
      driversAddRemarkInApp: [false],
      coDriverEnabled: [false],
      startHour: [1],
      endHour: [24],
      pCommentsToggle: [true],
      dCommentsToggle: [true],
      alertHour: [8],
      alertMinute: [0],
      doubleBookingActivated: [false],
      activateSignalColor: [true],
      askBirthDay: [false],
      emailNotificationsDriversDayPart: [false],
      emailNotificationsCoDriversDayPart: [false],
      emailNotificationsPlanners: [false],
      emailNotificationsParticipants: [false],
      emailNotificationsParticipantsRideConfirmed: [false],
      emailNotificationsParticipantsRideChanged: [false],
      emailNotificationsParticipantsRideDeleted: [false],
      emailNotificationsParticipantsRideReminder: [false],
      emailNotificationsParticipantsRideReminderAmount: [5],
      smsNotificationsParticipants: [false],
      smsNotificationsParticipantsRideConfirmed: [false],
      smsNotificationsParticipantsRideChanged: [false],
      smsNotificationsParticipantsRideDeleted: [false],
      smsNotificationsParticipantsRideReminder: [false],
      smsNotificationsParticipantsRideReminderAmount: [5],
      // emailNotificationsCCAddress: [[]],
      emailNotificationsBCCAddress: [[]],
      askGender: [false],
      ignoreDriverStatus: [false],
      characteristicsStatus: [false],
      customRideDuration: [15],
      customSegment: [15],
      showRideProduct: [true],
      noDeleteReason: [false],
      usesPlannerPlanning: [],
      orsApiKey: [],
      vehicleEntryDuration: [
        0,
        [Validators.max(15), CustomValidators.numberInput(false, false, 0)],
      ],
      vehicleExitDuration: [0, [Validators.max(15), CustomValidators.numberInput(false, false, 0)]],
      maxPassengerRideMinutes: [, [CustomValidators.numberInput(false, false, 0)]],
      usesDriverPlanning: [{ value: false, disabled: !this.isSuperUser }],
      usesSEPASystem: [{ value: false, disabled: !this.isSuperUser }],
      usesDriverRemarksInApp: [{ value: false, disabled: !this.isSuperUser }],
      usesUserNotificationSystem: [{ value: false, disabled: !this.isSuperUser }],
      usesAutomaticPlanning: [{ value: false, disabled: !this.isSuperUser }],
      usesAIChat: [{ value: false, disabled: !this.isSuperUser }],
      usesPrivateVehicles: [{ value: false, disabled: !this.isSuperUser }],
      usesEmployeePersonalDataManagement: [{ value: false, disabled: !this.isSuperUser }],
      usesEmployeeListForStaff: [{ value: false, disabled: !this.isSuperUser }],
    });

    const fileObject = {} as any;
    fileObject['favicon'] = this.publicSettings.favicon
      ? await getFileFromUrl(<string>this.publicSettings.favicon, `${this.organisationId}-favicon`)
      : null;

    fileObject['banner'] = this.publicSettings.banner
      ? await getFileFromUrl(<string>this.publicSettings.banner, `${this.organisationId}-banner`)
      : null;

    fileObject['logo'] = this.publicSettings.logo
      ? await getFileFromUrl(<string>this.publicSettings.logo, `${this.organisationId}-logo`)
      : null;

    fileObject['loginImage'] = this.publicSettings.loginImage
      ? await getFileFromUrl(
          <string>this.publicSettings.loginImage,
          `${this.organisationId}-loginImage`,
        )
      : null;

    if (dataStore.paidFunctionalities?.usesAutomaticPlanning) {
      this.organisationForm.addControl(
        'defaultStartLocation',
        this.fb.control([], defaultStartLocationValidator),
      );
      this.organisationForm.addControl(
        'defaultStartLocationInput',
        this.fb.control('', Validators.required),
      );
      this.organisationForm.addControl(
        'bufferPickup',
        this.fb.control({ value: 15, disabled: true }, [
          Validators.required,
          Validators.min(1),
          Validators.max(60),
        ]),
      );
      this.organisationForm.addControl(
        'bufferDropoff',
        this.fb.control({ value: 15, disabled: true }, [
          Validators.required,
          Validators.min(1),
          Validators.max(60),
        ]),
      );
    }

    this.organisationForm.patchValue({
      ...this.generalSettings,
      ...this.publicSettings,
      ...fileObject,
    });

    if (dataStore.paidFunctionalities) {
      this.organisationForm.patchValue({
        usesDriverPlanning: dataStore.paidFunctionalities.usesDriverPlanning ?? false,
        usesSEPASystem: dataStore.paidFunctionalities.usesSEPASystem ?? false,
        usesDriverRemarksInApp: dataStore.paidFunctionalities.usesDriverRemarksInApp ?? false,
        usesUserNotificationSystem:
          dataStore.paidFunctionalities.usesUserNotificationSystem ?? false,
        usesAutomaticPlanning: dataStore.paidFunctionalities.usesAutomaticPlanning ?? false,
        usesAIChat: dataStore.paidFunctionalities.usesAIChat ?? false,
        usesPrivateVehicles: dataStore.paidFunctionalities.usesPrivateVehicles ?? false,
        usesEmployeePersonalDataManagement:
          dataStore.paidFunctionalities.usesEmployeePersonalDataManagement ?? false,
        usesEmployeeListForStaff: dataStore.paidFunctionalities.usesEmployeeListForStaff ?? false,
      });
    }

    // if (this.generalSettings.emailNotificationsCCAddress) {
    //   this.organisationForm.controls.emailNotificationsCCAddress.setValue(
    //     this.parseEmails(this.generalSettings.emailNotificationsCCAddress),
    //   );
    // }
    if (this.generalSettings.emailNotificationsBCCAddress) {
      this.organisationForm.controls.emailNotificationsBCCAddress.setValue(
        this.parseEmails(this.generalSettings.emailNotificationsBCCAddress),
      );
    }

    if (dataStore.paidFunctionalities?.usesAutomaticPlanning) {
      this.organisationForm.controls.defaultStartLocationInput.setValue(
        this.organisationForm.value.defaultStartLocation?.address || '',
      );
      this.setupSuggestionsListener();
    }

    if (this.isSuperUser) {
      this.organisationForm.controls.name.enable();
      this.organisationForm.controls.maxActiveVehicles.enable();
      this.organisationForm.controls.bufferPickup?.enable();
      this.organisationForm.controls.bufferDropoff?.enable();
    }

    this.cpValue1 = this.organisationForm.controls.primaryColor.value;
    this.cpValue2 = this.organisationForm.controls.accentColor.value;
    this.cpValue3 = this.organisationForm.controls.signalColor.value;

    if (this.generalSettings.daypartSettings) {
      if (this.generalSettings.daypartSettings.startHour) {
        this.organisationForm.controls.startHour.setValue(
          this.generalSettings.daypartSettings.startHour,
        );
      }
      if (this.generalSettings.daypartSettings.endHour) {
        this.organisationForm.controls.endHour.setValue(
          this.generalSettings.daypartSettings.endHour,
        );
      }
    }

    if (this.generalSettings.alertDrivers) {
      if (this.generalSettings.alertDrivers.alertHour) {
        this.organisationForm.controls.alertHour.setValue(
          this.generalSettings.alertDrivers.alertHour,
        );
      }
      if (this.generalSettings.alertDrivers.alertMinute) {
        this.organisationForm.controls.alertMinute.setValue(
          this.generalSettings.alertDrivers.alertMinute,
        );
      }
    }

    const updatePinControls = (askPin: boolean) => {
      if (askPin) {
        this.organisationForm.get('pinAddCredits').enable();
        this.organisationForm.get('pinSubtractCredits').enable();
      } else {
        this.organisationForm.get('pinAddCredits').disable();
        this.organisationForm.get('pinSubtractCredits').disable();
      }
    };
    this.organisationForm.controls.askPin.valueChanges.subscribe(updatePinControls);
    updatePinControls(this.organisationForm.controls.askPin.value);

    this.organisationForm.controls.coDriverEnabled.valueChanges.subscribe(() => {
      if (this.organisationForm.controls.coDriverEnabled.value) {
        this.organisationForm.get('emailNotificationsCoDriversDayPart').enable();
      } else {
        this.organisationForm.get('emailNotificationsCoDriversDayPart').setValue(false);
        this.organisationForm.get('emailNotificationsCoDriversDayPart').disable();
      }
    });
    this.organisationForm.controls.coDriverEnabled.setValue(
      this.generalSettings.coDriverEnabled ?? false,
    );

    this.faviconPreview = this.organisationForm.controls.favicon.value
      ? URL.createObjectURL(this.organisationForm.controls.favicon.value)
      : null;
    this.organisationForm.controls.favicon.valueChanges.subscribe(() => {
      this.faviconPreview = this.organisationForm.controls.favicon.value
        ? URL.createObjectURL(this.organisationForm.controls.favicon.value)
        : null;
    });

    this.bannerPreview = this.organisationForm.controls.banner.value
      ? URL.createObjectURL(this.organisationForm.controls.banner.value)
      : null;
    this.organisationForm.controls.banner.valueChanges.subscribe(() => {
      this.bannerPreview = this.organisationForm.controls.banner.value
        ? URL.createObjectURL(this.organisationForm.controls.banner.value)
        : null;
    });

    this.logoPreview = this.organisationForm.controls.logo.value
      ? URL.createObjectURL(this.organisationForm.controls.logo.value)
      : null;
    this.organisationForm.controls.logo.valueChanges.subscribe(() => {
      this.logoPreview = this.organisationForm.controls.logo.value
        ? URL.createObjectURL(this.organisationForm.controls.logo.value)
        : null;
    });

    this.loginImagePreview = this.organisationForm.controls.loginImage.value
      ? URL.createObjectURL(this.organisationForm.controls.loginImage.value)
      : null;
    this.organisationForm.controls.loginImage.valueChanges.subscribe(() => {
      this.loginImagePreview = this.organisationForm.controls.loginImage.value
        ? URL.createObjectURL(this.organisationForm.controls.loginImage.value)
        : null;
    });

    redirectIfNotAdmin(this.router);
    this.loadingPage.set(false);
  }

  private parseEmails(value: any): string[] {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.map((e) => e.trim()).filter((e) => e.length > 0);
    }
    // Split by comma, space, semicolon, or newline
    return value
      .split(/[ ,;\n]+/)
      .map((e: string) => e.trim())
      .filter((e: string) => e.length > 0);
  }

  addEmail(event: MatChipInputEvent, controlName: string): void {
    const value = (event.value || '').trim();

    if (value) {
      const emails = this.parseEmails(value);

      const currentValues = this.organisationForm.get(controlName).value || [];
      const updatedValues = Array.isArray(currentValues) ? [...currentValues] : [];

      emails.forEach((email) => {
        if (!updatedValues.includes(email)) {
          updatedValues.push(email);
        }
      });

      this.organisationForm.get(controlName).setValue(updatedValues);
      this.organisationForm.get(controlName).markAsDirty();
    }

    event.chipInput!.clear();
  }

  removeEmail(email: string, controlName: string): void {
    const currentValues = this.organisationForm.get(controlName).value || [];
    if (Array.isArray(currentValues)) {
      const index = currentValues.indexOf(email);

      if (index >= 0) {
        currentValues.splice(index, 1);
        this.organisationForm.get(controlName).setValue([...currentValues]);
        this.organisationForm.get(controlName).markAsDirty();
      }
    }
  }

  openImportExport() {
    const dialogRef = this.dialog.open(ImportExportComponent, {
      width: '425px',
      data: { type: 'rides' },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && result.type === 'Export') {
        // export data
        this.transfering.set(true);
        await this.exportFirestoreDb(this.organisationId, result.startFrom, ['rides']); // for now hardcoded should be pulldown with checkboxes in dialog
        this.transfering.set(false);
      }
      if (result && result.type === 'Import') {
        // import data
        this.uploader.nativeElement.click();
      }
    });
  }

  openFileInput(id) {
    const element: HTMLElement = document.getElementById(id) as HTMLElement;
    element.click();
  }

  uploadedFile(event, id) {
    const file = event.target.files[0] as File;
    if (!file) {
      return;
    }
    switch (id) {
      case 'import':
        this.transfering.set(true);
        const reader = new FileReader();
        let promises = [];
        reader.readAsText(file);
        reader.onload = async () => {
          const importedJsonData = JSON.parse(reader.result as string);
          for (const collection in importedJsonData) {
            if (collection === 'rides') {
              for (const [key, value] of Object.entries(importedJsonData[collection])) {
                const rideData = value as Ride;
                rideData.start = new Date(rideData.start);
                rideData.end = new Date(rideData.end);
                rideData.autoUpdateDateTime = new Date(rideData.autoUpdateDateTime);
                rideData.lastAutoUpdate = new Date(rideData.lastAutoUpdate);

                rideData.vehicleRef = doc(
                  db,
                  `organisations/${this.organisationId}/vehicles/${rideData.vehicleId}`,
                );
                importedJsonData[collection][key] = rideData;
              }
            }
          }
          for (const [key, value] of Object.entries(importedJsonData)) {
            for (const [docKey, docValue] of Object.entries(value)) {
              promises.push(
                setDoc(doc(db, `organisations/${this.organisationId}/${key}/${docKey}`), docValue),
              );
              if (promises.length >= 100) {
                await Promise.allSettled(promises);
                promises = [];
              }
            }
          }
          if (promises.length > 0) {
            await Promise.allSettled(promises);
          }
          this.transfering.set(false);
          this.snackBar.open('De ritten zijn geïmporteerd.', 'X', {
            duration: 5000,
          });
        };
      case 'favicon':
      case 'banner':
      case 'logo':
      case 'loginImage':
        if (file.size > 2048000) {
          this.snackBar.open('Dit bestand mag niet groter zijn dan 2 MB.', 'X', {
            duration: 5000,
          });
          return;
        }
        this.organisationForm.controls[id].setValue(file);
        this.organisationForm.controls[id].markAsDirty();
        break;
    }
    event.target.value = null;
  }

  downloadFile(id) {
    const objectUrl = URL.createObjectURL(this.organisationForm.controls[id].value);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = objectUrl;
    a.download = `${this.organisationForm.controls[id].value.name}`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  deleteFile(fieldId: string) {
    this.organisationForm.controls[fieldId].setValue(null);
    this.organisationForm.controls[fieldId].markAsDirty();
  }

  previewImage(imageType: string) {
    switch (imageType) {
      case 'favicon':
        return this.faviconPreview;
      case 'banner':
        return this.bannerPreview;
      case 'logo':
        return this.logoPreview;
      case 'loginImage':
        return this.loginImagePreview;
    }
  }

  setupSuggestionsListener() {
    this.organisationForm.controls.defaultStartLocationInput.valueChanges
      .pipe(
        debounceTime(1000),
        switchMap((address: any) => {
          let addressString = address;
          if (address instanceof Object && address.address) {
            addressString = address.address;
          }
          if (!addressString || typeof addressString !== 'string') {
            return of([]);
          }
          const url = `https://api.openrouteservice.org/geocode/autocomplete?api_key=${dataStore.generalSettings.orsApiKey}&text=${addressString}&boundary.country=NL`;

          return this.http.get<any>(url).pipe(
            catchError((err) => {
              console.error('ORS API Error:', err);
              return of([]);
            }),
            map((response) => {
              return response?.features || {};
            }),
          );
        }),
      )
      .subscribe((features: any) => {
        const suggestionsArray: any[] = [];
        for (const feature of features) {
          const suggestion = feature.properties;
          let addressString = '';
          if (suggestion.layer === 'venue') {
            addressString = `${suggestion.name ?? ''} ${suggestion.street ?? ''} ${
              suggestion.housenumber ?? ''
            }, ${suggestion.postalcode ?? ''} ${suggestion.locality ?? ''}`;
          } else {
            addressString = `${suggestion.street ?? suggestion.name ?? ''} ${
              suggestion.housenumber ?? ''
            }, ${suggestion.postalcode ?? ''} ${suggestion.locality ?? ''}`;
          }
          if (!suggestionsArray.find((item) => item.address === addressString)) {
            suggestionsArray.push({
              address: addressString,
              long: feature.geometry.coordinates[0],
              lat: feature.geometry.coordinates[1],
            });
          }
        }
        this.defaultStartPrediction.set(suggestionsArray);
      });
  }

  async exportFirestoreDb(organisation: string, date: Date, collectionTypes: string[]) {
    try {
      console.log('Exporting....');
      const saveObj = {} as any;
      for (const collectionType of collectionTypes) {
        let queryConstraints = [];
        if (collectionType === 'rides') {
          queryConstraints.push(where('start', '>', date));
        }
        const docs = (
          await getDocs(
            query(
              collection(db, `organisations/${organisation}/${collectionType}`),
              ...queryConstraints,
            ),
          )
        ).docs;

        // need to build up the obj. Cannot set a value to property which parents are undefined
        saveObj[collectionType] = {};
        if (docs.length === 0) {
          return this.snackBar.open('Er zijn geen gegevens om te exporteren.', 'X', {
            duration: 5000,
          });
        }
        for (const newDoc of docs) {
          if (collectionType === 'rides') {
            let rideData = newDoc.data();
            rideData.end = new Date(rideData.end.toDate());
            rideData.start = new Date(rideData.start.toDate());
            rideData.autoUpdateDateTime = rideData.autoUpdateDateTime
              ? new Date(rideData.autoUpdateDateTime)
              : null;
            rideData.lastAutoUpdate = rideData.lastAutoUpdate
              ? new Date(rideData.lastAutoUpdate)
              : null;

            rideData.users = rideData.users ? rideData.users : null;
            rideData.rideProduct = rideData.rideProduct ? rideData.rideProduct : null;
            if (rideData.vehicleRef) {
              // cannot save ref to file. convert to string and recompile when importing
              rideData.vehicleRef = `organisations/${this.organisationId}/vehicles/${rideData.vehicleId}`;
            }
            saveObj[collectionType][newDoc.id] = rideData;
          } else {
            saveObj[collectionType][newDoc.id] = newDoc.data();
          }
        }
      }

      this._FileSaverService.save(
        new Blob([JSON.stringify(saveObj)]),
        `Backup-${this.organisationId} ${new Date().toLocaleDateString()}.json`,
      );
    } catch (e) {
      console.error(e);
    }
  }

  async save() {
    if (this.saving()) {
      return;
    }

    this.saving.set(true);
    this.organisationForm.markAllAsTouched();
    if (this.organisationForm.valid) {
      let saveObj = { ...this.organisationForm.value };
      delete saveObj.defaultStartLocationInput;
      const paidFunctionalityKeys = [
        'usesDriverPlanning',
        'usesSEPASystem',
        'usesDriverRemarksInApp',
        'usesUserNotificationSystem',
        'usesAutomaticPlanning',
        'usesAIChat',
        'usesPrivateVehicles',
        'usesEmployeePersonalDataManagement',
        'usesEmployeeListForStaff',
      ] as const;
      for (const key of paidFunctionalityKeys) {
        delete saveObj[key];
      }
      const startHour = saveObj.startHour;
      const endHour = saveObj.endHour;

      saveObj.daypartSettings = {
        startHour,
        endHour,
      };
      if (
        saveObj.alertHour !== undefined &&
        saveObj.alertMinute !== undefined &&
        saveObj.alertHour !== null &&
        saveObj.alertMinute !== null
      ) {
        saveObj.alertDrivers = {
          alertHour: Number(saveObj.alertHour),
          alertMinute: Number(saveObj.alertMinute),
        };
      }
      delete saveObj.alertHour;
      delete saveObj.alertMinute;
      let publicSaveObj = {};

      if (dataStore.paidFunctionalities?.usesAutomaticPlanning) {
        saveObj.vehicleExitDuration = Number(saveObj.vehicleExitDuration ?? 0);
        saveObj.vehicleEntryDuration = Number(saveObj.vehicleEntryDuration ?? 0);
        if (saveObj.bufferPickup && saveObj.bufferDropoff) {
          saveObj.bufferPickup = Number(saveObj.bufferPickup);
          saveObj.bufferDropoff = Number(saveObj.bufferDropoff);
        }
      }
      saveObj.maxPassengerRideMinutes = Number(saveObj.maxPassengerRideMinutes);
      if (saveObj.maxPassengerRideMinutes < 5) {
        saveObj.maxPassengerRideMinutes = null;
        this.organisationForm.controls.maxPassengerRideMinutes.setValue(null);
      }

      for (const key of Object.keys(saveObj)) {
        if (
          key.includes('NotificationsParticipants') ||
          key.includes('privacyLink') ||
          key.includes('askBirthDay') ||
          key.includes('askGender') ||
          key.includes('name') ||
          key.includes('primaryColor') ||
          key.includes('accentColor') ||
          key.includes('signalColor') ||
          key.includes('favicon') ||
          key.includes('banner') ||
          key.includes('logo') ||
          key.includes('loginImage')
        ) {
          if (key.includes('primaryColor')) {
            saveObj[key] = this.cpValue1;
          }
          if (key.includes('accentColor')) {
            saveObj[key] = this.cpValue2;
          }
          if (key.includes('signalColor')) {
            saveObj[key] = this.cpValue3;
          }
          if (
            key.includes('favicon') ||
            key.includes('banner') ||
            key.includes('logo') ||
            key.includes('loginImage')
          ) {
            delete saveObj[key];
          }

          if (saveObj[key] !== undefined) {
            if (saveObj[key] !== null) {
              publicSaveObj[key] = saveObj[key];
            } else {
              publicSaveObj[key] = deleteField();
            }
          }
        }
        if (saveObj[key] === null) {
          saveObj[key] = deleteField();
        }
      }

      try {
        if (saveObj?.name) {
          await updateDoc(doc(db, `organisations/${this.organisationId}`), { name: saveObj.name });
          this.titleService.setTitle(saveObj.name);
        }
        await setDoc(
          doc(db, `organisations/${this.organisationId}/settings/public`),
          publicSaveObj,
          { merge: true },
        );
        if (dataStore.generalSettings.coDriverEnabled && !saveObj.coDriverEnabled) {
          await this.removeCoDriverRights();
        }
        await setDoc(doc(db, `organisations/${this.organisationId}/settings/general`), saveObj, {
          merge: true,
        });
        if (dataStore.rootUser.rights === 'admin') {
          const updatedLinks = await this.uploadImageFiles();
          if (updatedLinks && Object.keys(updatedLinks).length > 0) {
            publicSaveObj = { ...publicSaveObj, ...updatedLinks };
            saveObj = { ...saveObj, ...updatedLinks };
          }
        }
        await logSettingsChange(this.generalSettings, saveObj, this.publicSettings, publicSaveObj);

        (document.querySelector('#favicon') as HTMLLinkElement).href = dataStore.theming.favicon;
        updateTheme({
          primary: saveObj.primaryColor,
          secondary: saveObj.accentColor,
        });

        this.snackBar.open('De organisatie is aangepast.', 'X', {
          duration: 5000,
        });
        if (this.isSuperUser) {
          const hasPaidFunctionalityChanges = paidFunctionalityKeys.some(
            (key) => this.organisationForm.get(key)?.dirty,
          );
          if (hasPaidFunctionalityChanges) {
            const raw = this.organisationForm.getRawValue();
            const paidFunctionalities: PaidFunctionalities = {
              usesDriverPlanning: raw.usesDriverPlanning,
              usesSEPASystem: raw.usesSEPASystem,
              usesDriverRemarksInApp: raw.usesDriverRemarksInApp,
              usesUserNotificationSystem: raw.usesUserNotificationSystem,
              usesAutomaticPlanning: raw.usesAutomaticPlanning,
              usesAIChat: raw.usesAIChat,
              usesPrivateVehicles: raw.usesPrivateVehicles,
              usesEmployeePersonalDataManagement: raw.usesEmployeePersonalDataManagement,
              usesEmployeeListForStaff: raw.usesEmployeeListForStaff,
            };
            await setDoc(
              doc(db, `organisations/${this.organisationId}`),
              { paidFunctionalities },
              { merge: true },
            );
            if (!paidFunctionalities.usesSEPASystem) {
              await this.disableSepa();
            }
            if (!paidFunctionalities.usesDriverRemarksInApp) {
              await this.disableDriverRemarksInApp();
            }
            if (!paidFunctionalities.usesUserNotificationSystem) {
              await this.disableUserNotifications();
            }
            location.reload();
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    this.saving.set(false);
  }

  async removeCoDriverRights() {
    let promises: any[] = [];
    const userDocs = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/users`),
        where('roles', 'array-contains', Role.CoDriver),
      ),
    );
    for (const userDoc of userDocs.docs) {
      const data = userDoc.data() as User;
      let newRoles = (data.roles || []).filter((r) => r !== Role.CoDriver);
      promises.push(
        updateDoc(doc(db, `organisations/${this.organisationId}/users/${userDoc.id}`), {
          roles: newRoles,
        }),
      );
      if (promises.length >= 100) {
        await Promise.allSettled(promises);
        promises = [];
      }
    }
    if (promises.length > 0) {
      await Promise.allSettled(promises);
    }
  }

  async uploadImageFiles() {
    const imageKeys = ['favicon', 'banner', 'logo', 'loginImage'];
    const updateLinks = {};
    for (const key of imageKeys) {
      if (this.organisationForm.controls[key].dirty) {
        if (this.organisationForm.controls[key].value === null) {
          updateLinks[key] = deleteField();
          continue;
        }
        const filePath = `organisations/${this.organisationId}/organisation_images/${this.organisationId}-${key}.${getFileExtension(this.organisationForm.controls[key].value)}`;
        const storageRef = ref(storage, filePath);
        const uploadResult = await uploadBytes(
          storageRef,
          this.organisationForm.controls[key].value,
        );
        const downloadUrl = await getDownloadURL(uploadResult.ref);
        updateLinks[key] = downloadUrl;
      }
    }
    if (Object.keys(updateLinks).length > 0) {
      await setDoc(doc(db, `organisations/${this.organisationId}/settings/general`), updateLinks, {
        merge: true,
      });
      await setDoc(doc(db, `organisations/${this.organisationId}/settings/public`), updateLinks, {
        merge: true,
      });
      return updateLinks;
    }
    return {};
  }

  private async disableSepa() {
    await setDoc(
      doc(db, `organisations/${this.organisationId}/settings/public`),
      { sepaEnabled: false },
      { merge: true },
    );
    await setDoc(
      doc(db, `payment-configs/${this.organisationId}`),
      { sepa_enabled: false },
      { merge: true },
    );
  }

  private async disableDriverRemarksInApp() {
    await setDoc(
      doc(db, `organisations/${this.organisationId}/settings/general`),
      { driversAddRemarkInApp: false },
      { merge: true },
    );
  }

  private async disableUserNotifications() {
    await setDoc(
      doc(db, `organisations/${this.organisationId}/settings/general`),
      { emailNotificationsParticipants: false, smsNotificationsParticipants: false },
      { merge: true },
    );
    await setDoc(
      doc(db, `organisations/${this.organisationId}/settings/public`),
      { emailNotificationsParticipants: false, smsNotificationsParticipants: false },
      { merge: true },
    );
  }

  async exportRides() {
    const allCharacteristics = [];
    if (this.generalSettings.characteristicsStatus) {
      const characteristicsDocs = await getDocs(
        collection(db, `organisations/${this.organisationId}/characteristics`),
      );
      characteristicsDocs.forEach((doc) => {
        allCharacteristics.push(doc.data() as Characteristic);
      });
    }
    const dialogRef = this.dialog.open(ExportRidesComponent, {
      width: '400px',
      data: {
        characteristics: allCharacteristics,
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      console.log('result', result);
      if (!result) {
        return false;
      }
      const ridesToExport = [];
      const allVehicles: Vehicle[] = [];
      const allUsers: User[] = [];
      const allParticularities: Particularity[] = dataStore.selectedOrganisation.particularities;
      const vehicleDocs = await getDocs(
        collection(db, `organisations/${this.organisationId}/vehicles`),
      );
      vehicleDocs.forEach((doc) => {
        allVehicles.push(doc.data() as Vehicle);
      });
      const userDocs = await getDocs(collection(db, `organisations/${this.organisationId}/users`));
      userDocs.forEach((doc) => {
        let user = doc.data() as User;
        user = { ...user, id: doc.id };
        allUsers.push(user);
      });
      let queryConstraints = [];
      queryConstraints.push(where('finished', '==', true));
      switch (result.type) {
        case 'year':
          queryConstraints.push(where('year', '==', result.year));
          break;
        case 'month':
          queryConstraints.push(where('year', '==', result.year));
          queryConstraints.push(where('month', '==', result.month));
          break;
        case 'week':
          queryConstraints.push(where('week', '==', result.week));
          break;
        case 'day':
          queryConstraints.push(where('year', '==', result.year));
          queryConstraints.push(where('month', '==', result.month));
          queryConstraints.push(where('day', '==', result.day));
          break;
      }
      const ridesDocs = await getDocs(
        query(collection(db, `organisations/${this.organisationId}/rides`), ...queryConstraints),
      );
      const rides: Ride[] = [];
      ridesDocs.forEach((doc) => {
        let ride = doc.data() as Ride;
        ride = { ...ride, id: doc.id };
        rides.push(ride);
      });

      try {
        for (const ride of rides) {
          if (result.characteristics.length > 0) {
            const characteristicsFound = ride.characteristics.filter(
              (item) => result.characteristics.indexOf(item) != -1,
            );
            if (characteristicsFound.length <= 0) {
              continue;
            }
          }
          const exportRideObj = {};

          exportRideObj['Maand'] = 0;
          exportRideObj['Week'] = 0;
          exportRideObj['Dag'] = '-';
          exportRideObj['Starttijd'] = '';
          exportRideObj['Oorspronkelijke ophaaltijd'] = '';
          exportRideObj['Oorspronkelijke afzettijd'] = '';
          exportRideObj['Voertuig'] = '';
          exportRideObj['Chauffeur'] = '';
          exportRideObj['Kenmerken'] = '';
          exportRideObj['Kaartnummer'] = '';
          exportRideObj['Naam'] = '';
          exportRideObj['Passagier ID'] = '';
          exportRideObj['Geslacht'] = '';
          if (this.publicSettings.askBirthDay) {
            exportRideObj['Leeftijd'] = '';
          }
          exportRideObj['Bijzonderheden'] = '';
          exportRideObj['Ritprijs'] = 0;
          exportRideObj['Betaald'] = 'Nee';
          exportRideObj['Betaalde Credits'] = 0;
          exportRideObj['Rit ID'] = '';

          if (result.exportFromAndTo) {
            exportRideObj['Ophaaladres'] = '';
            exportRideObj['Bestemmingsadres'] = '';
          }
          exportRideObj['Instaptijd'] = '';
          exportRideObj['Uitstaptijd'] = '';

          if (ride.week) {
            exportRideObj['Week'] = parseInt(ride.week.substring(ride.week.indexOf('-') + 1));
          }

          if (ride.start) {
            moment.locale('nl');
            const day = moment(ride.start.toDate()).locale('nl').format('dddd');
            exportRideObj['Dag'] = day.charAt(0).toUpperCase() + day.slice(1, 2);

            const month = moment(ride.start.toDate()).locale('nl').format('MMMM');
            exportRideObj['Maand'] = month.charAt(0).toUpperCase() + month.slice(1);

            exportRideObj['Starttijd'] = `${moment(ride.start.toDate()).format(
              'DD-MM-YYYY HH:mm',
            )}`;
          }

          if (ride.vehicleId) {
            allVehicles.find((vehicle) => {
              if (vehicle.id === ride.vehicleId) {
                exportRideObj['Voertuig'] = vehicle.name;
              }
            });
          }

          if (ride.driverId) {
            allUsers.find((user) => {
              if (user.id === ride.driverId) {
                exportRideObj['Chauffeur'] = user.name;
              }
            });
          }

          if (ride.characteristics && ride.characteristics.length > 0) {
            exportRideObj['Kenmerken'] = ride.characteristics.toString().replaceAll(',', ', ');
          }

          if (ride.rideProduct) {
            exportRideObj['Ritprijs'] = ride.rideProduct.cost;
          }

          exportRideObj['Rit ID'] = `${ride.id}`;

          if (ride.users && Object.keys(ride.users).length > 0) {
            for (const userInRide of Object.values(ride.users)) {
              let pickup = '';
              let destination = '';
              if (userInRide.from) {
                pickup =
                  typeof userInRide.from === 'string' ? userInRide.from : userInRide.from.address;
              }

              if (userInRide.to) {
                destination =
                  typeof userInRide.to === 'string' ? userInRide.to : userInRide.to.address;
              }

              const currentUser = allUsers.find((user) => {
                return user.id === userInRide.id;
              });
              const newExportRideObj = { ...exportRideObj };

              if (ride.automaticallyPlanned === true) {
                const originalPickup = valueToDate(userInRide.originalUserPickUpTime);
                const originalDropoff = valueToDate(userInRide.originalUserDropOffTime);
                newExportRideObj['Oorspronkelijke ophaaltijd'] = originalPickup
                  ? moment(originalPickup).format('HH:mm')
                  : '';
                newExportRideObj['Oorspronkelijke afzettijd'] = originalDropoff
                  ? moment(originalDropoff).format('HH:mm')
                  : '';
              }

              newExportRideObj['Naam'] = currentUser ? currentUser.name : userInRide.title;
              newExportRideObj['Passagier ID'] = currentUser ? currentUser.id : userInRide.id;
              if (currentUser?.cardId) {
                newExportRideObj['Kaartnummer'] = currentUser.cardId;
              }
              if (currentUser?.gender) {
                newExportRideObj['Geslacht'] = currentUser.gender;
              }

              if (exportRideObj['Leeftijd'] && currentUser?.birthDate) {
                let diff = Date.now() - currentUser.birthDate.toDate();
                let ageDate = new Date(diff);
                newExportRideObj['Leeftijd'] = Math.abs(ageDate.getUTCFullYear() - 1970);
              }

              if (currentUser?.particularityIds) {
                let particularityNames = [];
                for (const particularityId of currentUser.particularityIds) {
                  const particularity = allParticularities.find(
                    (particularity) => particularity.id === particularityId,
                  );
                  if (particularity) {
                    particularityNames.push(particularity.name);
                  }
                }
                if (particularityNames) {
                  newExportRideObj['Bijzonderheden'] = particularityNames.join(', ');
                }
              }

              if (result.exportFromAndTo) {
                newExportRideObj['Ophaaladres'] = pickup;
                newExportRideObj['Bestemmingsadres'] = destination;
              }
              newExportRideObj['Instaptijd'] = currentUser?.vehicleEntryDuration ?? '';
              newExportRideObj['Uitstaptijd'] = currentUser?.vehicleExitDuration ?? '';
              if (userInRide.paid) {
                newExportRideObj['Betaald'] = 'ja';
                newExportRideObj['Betaalde Credits'] = userInRide.charge.creditsSpent;
              }
              if (userInRide.rideProduct) {
                newExportRideObj['Ritprijs'] = userInRide.rideProduct.cost;
              }
              ridesToExport.push(newExportRideObj);
            }
          } else {
            ridesToExport.push(exportRideObj);
          }
        }
      } catch (error) {
        console.error(error);
      }
      if (ridesToExport.length > 0) {
        let fileName = 'Ritten.xlsx';
        let monthName;
        switch (result.type) {
          case 'year':
            fileName = `Ritten ${result.year}.xlsx`;
            break;
          case 'month':
            monthName = moment.months(result.month);
            fileName = `Ritten ${monthName} ${result.year}.xlsx`;
            break;
          case 'week':
            fileName = `Ritten week ${result.week}.xlsx`;
            break;
          case 'day':
            monthName = moment.months(result.month);
            fileName = `Ritten ${result.day} ${monthName} ${result.year}.xlsx`;
            break;
        }
        this.sortExportRidesOutput(ridesToExport);
        await exportJsonToExcel(ridesToExport, fileName, 'Rides');
      } else {
        // Nothing to export
        this.snackBar.open('Er zijn geen ritten om te exporteren.', 'X', {
          duration: 5000,
        });
      }
    });
  }

  sortExportRidesOutput(ridesArray) {
    ridesArray.sort((row1, row2) => {
      if (row1['Week'] < row2['Week']) {
        return -1;
      }
      if (row1['Week'] > row2['Week']) {
        return 1;
      }
      if (row1['Week'] === row2['Week']) {
        if (row1['Starttijd'] < row2['Starttijd']) {
          return -1;
        }
        if (row1['Starttijd'] > row2['Starttijd']) {
          return 1;
        }
      }
      return 0;
    });
  }

  onSuggestionSelected(event: any) {
    this.organisationForm.patchValue({ defaultStartLocation: event.option.value });
    this.organisationForm.controls.defaultStartLocationInput.setValue(event.option.value.address);
  }

  onDefaultStartLocationBlur() {
    if (this.organisationForm.value.defaultStartLocation) {
      this.organisationForm.controls.defaultStartLocationInput.setValue(
        this.organisationForm.value.defaultStartLocation.address,
      );
    }
  }
}

function defaultStartLocationValidator(control: AbstractControl) {
  const value = control.value;
  if (
    value &&
    typeof value === 'object' &&
    value.address &&
    value.long !== undefined &&
    value.lat !== undefined
  ) {
    return null;
  }
  return { invalidLocation: true };
}
